import { z } from "zod";
import { optional, storableText } from "./appointments";
import { recordId } from "./contracts";
import { absentOrBlankAsNull, searchTextUpTo, unset } from "./fields";
import { propertyReadQuery, propertyType } from "./properties";

export const RELATIONSHIP_PAGE_SIZE_MAX = 100;
export const LINKABLE_PROPERTY_PAGE_SIZE_MAX = 500;
export const CONTACT_NAME_MAX_LENGTH = 200;
export const CONTACT_EMAIL_MAX_LENGTH = 320;
export const CONTACT_PHONE_MAX_LENGTH = 50;
export const CONTACT_DESIGNATION_MAX_LENGTH = 200;

const SEARCH_MAX_LENGTH = 200;

const searchText = searchTextUpTo(SEARCH_MAX_LENGTH);

const pageOf = (maxSize: number) => ({
  page: z.preprocess(unset, z.coerce.number().int().min(1).max(10_000).default(1)),
  pageSize: z.preprocess(unset, z.coerce.number().int().min(1).max(maxSize).default(25)),
});

const sortedBy = {
  sort: z.preprocess(unset, z.enum(["name", "email"]).default("name")),
  order: z.preprocess(unset, z.enum(["asc", "desc"]).default("asc")),
};

export const customerContact = z.object({
  name: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  designation: z.string().nullable(),
});
export type CustomerContact = z.infer<typeof customerContact>;

export const customer = z.object({
  id: recordId,
  name: z.string(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  image: z.string().nullable(),
  description: z.string().nullable(),
  website: z.string().nullable(),
  contact: customerContact.nullable().optional(),
});
export type Customer = z.infer<typeof customer>;

export const customerReadQuery = propertyReadQuery;
export type CustomerReadQuery = z.infer<typeof customerReadQuery>;

export const customerListQuery = z.object({ search: optional(searchText), ...sortedBy, ...pageOf(RELATIONSHIP_PAGE_SIZE_MAX) });
export type CustomerListQuery = z.infer<typeof customerListQuery>;

export const customerPage = z.object({ items: z.array(customer), total: z.number().int().nonnegative() });
export type CustomerPage = z.infer<typeof customerPage>;

const optionalText = (maxLength: number) => z.preprocess(absentOrBlankAsNull, storableText(maxLength).nullable());

export const customerContactRequest = z
  .object({
    name: storableText(CONTACT_NAME_MAX_LENGTH),
    email: z.string().trim().max(CONTACT_EMAIL_MAX_LENGTH).email(),
    phone: optionalText(CONTACT_PHONE_MAX_LENGTH),
    designation: optionalText(CONTACT_DESIGNATION_MAX_LENGTH),
  })
  .strict();
export type CustomerContactRequest = { name: string; email: string; phone?: string | null; designation?: string | null };

export const vendorService = z.object({ id: recordId, name: z.string(), price: z.number().nullable() });
export type VendorService = z.infer<typeof vendorService>;

export const vendor = z.object({
  id: recordId,
  name: z.string(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  image: z.string().nullable(),
  services: z.array(vendorService),
});
export type Vendor = z.infer<typeof vendor>;

export const vendorListQuery = z.object({ search: optional(searchText), service: optional(searchText), ...sortedBy, ...pageOf(RELATIONSHIP_PAGE_SIZE_MAX) });
export type VendorListQuery = z.infer<typeof vendorListQuery>;

export const vendorPage = z.object({ items: z.array(vendor), total: z.number().int().nonnegative() });
export type VendorPage = z.infer<typeof vendorPage>;

export const linkableProperty = z.object({
  id: recordId,
  name: z.string(),
  address: z.string().nullable(),
  types: z.array(propertyType),
});
export type LinkableProperty = z.infer<typeof linkableProperty>;

export const servicedProperty = linkableProperty.extend({ linkedAt: z.string() });
export type ServicedProperty = z.infer<typeof servicedProperty>;

export const servicedPropertyQuery = z.object(pageOf(LINKABLE_PROPERTY_PAGE_SIZE_MAX));
export type ServicedPropertyQuery = z.infer<typeof servicedPropertyQuery>;

export const servicedPropertyPage = z.object({ items: z.array(servicedProperty), total: z.number().int().nonnegative() });
export type ServicedPropertyPage = z.infer<typeof servicedPropertyPage>;

export const linkablePropertyQuery = z.object({ search: optional(searchText), ...pageOf(LINKABLE_PROPERTY_PAGE_SIZE_MAX) });
export type LinkablePropertyQuery = z.infer<typeof linkablePropertyQuery>;

export const linkablePropertyPage = z.object({ items: z.array(linkableProperty), total: z.number().int().nonnegative() });
export type LinkablePropertyPage = z.infer<typeof linkablePropertyPage>;

export const servicedPropertyRequest = z.object({ property: recordId.toLowerCase() }).strict();
export type ServicedPropertyRequest = z.infer<typeof servicedPropertyRequest>;
